import { ArrowRight, Search } from 'lucide-react'
import { useCatalog, useCurrency, useStoreContent, useProductLink, price, SORT_OPTIONS, isAvailable, productImage } from '../shared'
import StockBadge from '../../../../components/StockBadge'
import { t } from '../../../../i18n'

// Editorial lookbook: full-bleed hero, text-only category nav, one large
// featured piece, then a quiet two-column grid of big photographs with
// numbered captions - reads like a magazine spread, not a product grid.
export default function StoreMinimalTemplate({ menu, addItem, storeName }) {
  const { filtered, categories, search, setSearch, category, setCategory, sort, setSort } = useCatalog(menu)
  const currency = useCurrency()
  const open = useProductLink()
  const content = useStoreContent({ storeName, title: 'Considered pieces, chosen slowly.', subtitle: 'A small, edited collection. Nothing extra.' })
  const heroImage = content.heroImage || productImage(filtered.find(productImage) || {})
  const [featured, ...rest] = filtered

  return (
    <div className="editorial" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <section className="relative h-[62vh] min-h-[380px] rounded-sm overflow-hidden bg-stone-200 mb-14">
        {heroImage && <img src={heroImage} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute bottom-0 start-0 end-0 p-8 md:p-14 text-white">
          <p className="text-[11px] tracking-[0.35em] uppercase mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>{content.name}</p>
          <h1 className="text-4xl md:text-6xl font-light leading-[1.05] max-w-3xl italic">{content.heroTitle}</h1>
          <p className="mt-5 max-w-lg text-white/80 text-base">{content.heroSubtitle}</p>
        </div>
      </section>

      <nav className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-y border-stone-300 py-5 mb-12" aria-label={t("Collections")} style={{ fontFamily: 'Inter, sans-serif' }}>
        {[{ id: 'all', name: t('All') }, ...categories].map(c => (
          <button key={c.id} onClick={() => setCategory(String(c.id))} aria-pressed={category === String(c.id)}
            className={`text-xs tracking-[0.2em] uppercase pb-1 border-b ${category === String(c.id) ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-900'}`}>
            {c.name}
          </button>
        ))}
        <label className="flex items-center gap-2 border-b border-stone-300 text-stone-500">
          <Search size={14} />
          <input aria-label={t("Search the collection")} value={search} onChange={e => setSearch(e.target.value)} placeholder={t("Search")} className="bg-transparent outline-none text-xs tracking-widest uppercase w-28 py-1" />
        </label>
        <select aria-label={t("Sort")} value={sort} onChange={e => setSort(e.target.value)} className="bg-transparent text-xs tracking-widest uppercase text-stone-500 outline-none">
          {SORT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
        </select>
      </nav>

      {!featured ? (
        <p className="text-center text-stone-500 italic py-20">{t("Nothing here yet - try another collection.")}</p>
      ) : (
        <>
          <article className="grid md:grid-cols-5 gap-8 md:gap-14 items-center mb-20">
            <button onClick={() => open(featured)} className="md:col-span-3 aspect-[4/5] bg-stone-100 overflow-hidden" aria-label={t("View {name}", { name: featured.name })}>
              {productImage(featured) && <img src={productImage(featured)} alt={featured.name} className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700" />}
            </button>
            <div className="md:col-span-2">
              <p className="text-[11px] tracking-[0.3em] uppercase text-stone-500" style={{ fontFamily: 'Inter, sans-serif' }}>{t("The edit · No. 01 ·")} {featured.categoryName}</p>
              <h2 className="text-3xl md:text-4xl mt-4 font-light">{featured.name}</h2>
              {featured.description && <p className="mt-5 text-stone-600 leading-relaxed">{t(featured.description)}</p>}
              <p className="mt-6 text-lg">{price(featured.price, currency)}</p>
              <StockBadge item={featured} className="mt-3" />
              <div className="flex gap-6 mt-8 text-xs tracking-[0.2em] uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
                <button onClick={() => open(featured)} className="flex items-center gap-2 border-b border-stone-900 pb-1">{t("Discover")} <ArrowRight size={14} /></button>
                {isAvailable(featured) && <button onClick={() => addItem(featured)} className="border-b border-stone-400 text-stone-500 pb-1 hover:text-stone-900">{t("Add to bag")}</button>}
              </div>
            </div>
          </article>

          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-16">
            {rest.map((item, i) => (
              <article key={item.id} className={i % 2 === 1 ? 'sm:mt-24' : ''}>
                <button onClick={() => open(item)} className="block w-full aspect-[4/5] bg-stone-100 overflow-hidden" aria-label={t("View {name}", { name: item.name })}>
                  {productImage(item) && <img loading="lazy" src={productImage(item)} alt={item.name} className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700" />}
                </button>
                <div className="flex items-baseline justify-between mt-5 gap-4">
                  <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400" style={{ fontFamily: 'Inter, sans-serif' }}>{t("No.")} {String(i + 2).padStart(2, '0')}</p>
                    <button onClick={() => open(item)} className="text-xl font-light mt-1 text-start">{item.name}</button>
                  </div>
                  <span className="text-stone-600 whitespace-nowrap">{price(item.price, currency)}</span>
                </div>
                <StockBadge item={item} className="mt-3" />
                {isAvailable(item) && (
                  <button onClick={() => addItem(item)} className="mt-3 text-[11px] tracking-[0.2em] uppercase text-stone-500 border-b border-stone-300 hover:text-stone-900" style={{ fontFamily: 'Inter, sans-serif' }}>{t("Add to bag")}</button>
                )}
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
